// Friendly random usernames ("SunnyOtter27") and DiceBear avatars. Pure, so the web app imports it too.
import { msg } from './i18n.js';

const ADJECTIVES = [
  'Brave',
  'Lucky',
  'Sunny',
  'Swift',
  'Clever',
  'Cosmic',
  'Mighty',
  'Jolly',
  'Bold',
  'Calm',
  'Witty',
  'Happy',
  'Zesty',
  'Golden',
  'Rapid',
  'Quiet',
  'Fuzzy',
  'Nimble',
  'Breezy',
  'Chill',
  'Dandy',
  'Epic',
  'Frosty',
  'Gentle',
  'Hyper',
  'Keen',
  'Lively',
  'Mellow',
  'Noble',
  'Peppy',
  'Plucky',
  'Quick',
  'Rosy',
  'Silver',
  'Snappy',
  'Spicy',
  'Steady',
  'Super',
  'Turbo',
  'Vivid',
  'Wild',
  'Zippy',
  'Atomic',
  'Bouncy',
  'Crispy',
  'Daring',
  'Electric',
  'Fancy',
  'Glowing',
  'Humble',
] as const;

const NOUNS = [
  'Otter',
  'Panda',
  'Falcon',
  'Tiger',
  'Fox',
  'Koala',
  'Lynx',
  'Raven',
  'Wolf',
  'Badger',
  'Bison',
  'Comet',
  'Dragon',
  'Eagle',
  'Gecko',
  'Hawk',
  'Jaguar',
  'Llama',
  'Mango',
  'Moose',
  'Narwhal',
  'Owl',
  'Panther',
  'Pixel',
  'Puffin',
  'Quokka',
  'Rocket',
  'Shark',
  'Sloth',
  'Sparrow',
  'Squid',
  'Taco',
  'Toucan',
  'Turtle',
  'Walrus',
  'Yak',
  'Zebra',
  'Bean',
  'Cactus',
  'Cookie',
  'Dolphin',
  'Ferret',
  'Hippo',
  'Kiwi',
  'Lemur',
  'Maple',
  'Nugget',
  'Pebble',
  'Penguin',
  'Waffle',
] as const;

const pick = <T>(xs: readonly T[], r: number) => xs[r % xs.length]!;

export function randomUsername(): string {
  const r = crypto.getRandomValues(new Uint32Array(3));
  return `${pick(ADJECTIVES, r[0]!)}${pick(NOUNS, r[1]!)}${10 + (r[2]! % 90)}`;
}

export const USERNAME_RULE = /^[A-Za-z0-9_.]{3,20}$/;

/** Names that would let someone pass as staff in support chats, emails or the leaderboard. */
const RESERVED =
  /^(admin|administrator|support|staff|moderator|mod|gaintplay|system|official|help)/i;

export function isReservedUsername(name: string): boolean {
  return RESERVED.test(name.replace(/[_.]/g, ''));
}

/** DiceBear avatar seeded by the username, so a name always brings the same face. */
export function avatarUrlFor(seed: string): string {
  return `https://api.dicebear.com/9.x/lorelei-neutral/svg?seed=${encodeURIComponent(seed)}&backgroundColor=ffe8d9,f6f2ea,ffd5b8,e9f4ec,fff1c7`;
}

/**
 * "Show my name on the live feed and leaderboard" for a new account. On, as the SPEC says; the
 * owner may switch the default to off here. That affects new sign-ups only: existing accounts keep
 * what they have (the column's own default, used by nothing else, stays on).
 */
export const PUBLIC_NAME_DEFAULT = true;

/**
 * The name the public sees on the live feed and the leaderboard. When the owner turned "Show my
 * name" off, only the first two characters are left: enough to tell rows apart, not to find them.
 */
export function publicName(username: string, isPublic: boolean): string {
  return isPublic ? username : `${username.slice(0, 2)}***`;
}

/**
 * Shown in place of a deleted account's name wherever it still appears (other users' lists).
 * Marked for translation: whoever shows it translates it (the API with tr, the web with t).
 */
// i18n: shown in place of the name of a friend who deleted their account, in your list of invited friends
export const DELETED_NAME = msg('Deleted account');

/**
 * What a deleted account's past live-feed items say instead of its name. Stored in the feed as this
 * English text (usernames can't contain a space, so it can't clash with one) and translated where
 * it's shown.
 */
// i18n: shown on the live feed of earnings in place of the name of someone who deleted their account
export const FEED_DELETED_NAME = msg('A member');
